import React, { useMemo, useState } from 'react'
import { Modal, Button } from 'react-bootstrap'
import 'lib/reactBootstrapReact19/modal'
import 'lib/reactBootstrapReact19/button'
import _sortBy from 'lodash/sortBy'
import Select from 'react-select'
import styles from './index.module.scss'
import { useDispatch } from 'react-redux'
import { customStyles } from './styles'
import { validateB2BPartnerCodeRoute } from 'dataSource/ProviderSearch/routes'
import useApi from 'hooks/useApi'
import { modifyURLQuery } from 'utils/common'
import { useRouter } from 'next/router'
import _omit from 'lodash/omit'
import { setB2BDetails } from 'state/booking/actions'

type B2bVerifyFormModalProps = {
  businessPartners: B2bPartnerOptionDetails[]
  handleChangeB2bFormModal: (updatedValue: boolean) => void
  handleChangeB2bMessageModal: (updatedValue: boolean) => void
}

const B2bVerifyFormModal: React.FC<B2bVerifyFormModalProps> = ({
  businessPartners,
  handleChangeB2bFormModal,
  handleChangeB2bMessageModal,
}) => {
  const router = useRouter()
  const { request: businessPartnerRequest } = useApi()
  const dispatch = useDispatch()
  const [selectedKeyword, setSelectedKeyword] = useState<string>('')
  const [
    selectedBusinessPartner,
    setSelectedBusinessPartner,
  ] = useState<B2bPartnerOptionDetails>(null)
  const { name: selectedBusinessPartnerName = '' } =
    selectedBusinessPartner?.value ?? {}

  /**
   * Verify Keyword
   * @returns {isValid, businessPartnerId}
   */
  const verifyKeyword = async (): Promise<VerifyKeywordResponse> => {
    return await businessPartnerRequest.post(
      validateB2BPartnerCodeRoute(selectedKeyword, selectedBusinessPartnerName)
    )
  }

  const handleSubmit = async () => {
    const { isValid, businessPartnerId } = await verifyKeyword()

    if (isValid && businessPartnerId) {
      const businessPartner: BusinessPartner = businessPartners.filter(
        (bp) => bp.value.id === businessPartnerId
      )[0].value
      const {
        id,
        name,
        hideInsuranceInput,
        hidePaymentInput,
        hidePostBookingModalCopy,
        isCreditCardOptional,
        customSuccessMessage,
        authorizationRequired,
        channelType,
        dealStructure,
        priorAuthConfiguration,
      } = businessPartner ?? {}

      const partnerDetails: B2BDetails = {
        partnerId: id,
        partnerName: name,
        isB2BUser: isValid,
        keyword: selectedKeyword,
        flags: {
          hideInsurance: hideInsuranceInput,
          hidePayment: hidePaymentInput,
          hidePostBookingModalCopy: hidePostBookingModalCopy,
          isCreditCardOptional,
        },
        verificationMessages: {
          customSuccessMessage,
        },
        authorizationRequired,
        ...(channelType && { channelType }),
        ...(dealStructure && { dealStructure }),
        ...(priorAuthConfiguration && {
          priorAuthConfiguration,
        }),
      }

      dispatch(setB2BDetails(partnerDetails))
    }

    modifyURLQuery(router, {
      partnershipCode: selectedKeyword,
    })
    handleChangeB2bFormModal(false)
    handleChangeB2bMessageModal(true)
  }

  const onBusinessPartnerChange = (
    businessPartnerOption: B2bPartnerOptionDetails
  ) => {
    setSelectedBusinessPartner(businessPartnerOption)

    if (selectedKeyword) {
      setSelectedKeyword('')
    }
  }

  const onKeywordChange = (keywordOption: B2bPartnerOptionDetails) => {
    setSelectedKeyword(keywordOption.value.partnershipCode)
  }

  /*
    Filter allBusinessPartnerList array to get the list of business
    partners with unique partner company names
  */
  const businessPartnerOptions = useMemo(() => {
    const b2bPartnerOptions =
      businessPartners?.filter((b2bPartnerObj, index) => {
        const { isActive = false } = b2bPartnerObj?.value ?? {}
        const b2bPartnerInitialIndex = businessPartners?.findIndex(
          ({ label: b2bPartnerItemLabel, value: b2bPartnerItemValue }) =>
            b2bPartnerObj?.label === b2bPartnerItemLabel &&
            !!b2bPartnerItemValue?.isActive
        )
        return isActive && index === b2bPartnerInitialIndex
      }) ?? []
    const b2bPartnerOptionsSorted = _sortBy(b2bPartnerOptions, [
      (businessPartnerObj) => businessPartnerObj?.value?.name?.toLowerCase(),
    ])
    return b2bPartnerOptionsSorted
  }, [businessPartners])

  const selectedPartnerKeywordOptions = useMemo(() => {
    const keywordOptions = businessPartners?.reduce(
      (businessPartnerKeywordAcc, businessPartnerOption) => {
        const b2bPartnerOptionDetails = businessPartnerOption.value
        const {
          partnershipCode,
          name: partnerName,
          isActive,
        } = b2bPartnerOptionDetails

        if (partnerName === selectedBusinessPartner?.value?.name && isActive) {
          businessPartnerKeywordAcc = [
            ...businessPartnerKeywordAcc,
            { label: partnershipCode, value: businessPartnerOption.value },
          ]
        }

        return businessPartnerKeywordAcc
      },
      []
    )
    return keywordOptions
  }, [businessPartnerOptions, selectedBusinessPartner])

  return (
    <Modal
      centered
      dialogClassName={styles.b2bModal}
      show={true}
      backdrop="static"
      onHide={() => handleChangeB2bFormModal(false)}
      contentClassName={styles.b2bVerifyModalContentVerifyForm}
    >
      <Modal.Header className={styles.b2bVerifyFormModalHeader} closeButton>
        <Modal.Title className={styles.b2bVerifyFormModalTitle}>
          Enter client details
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ borderBottom: 'none' }}>
        <div className={styles.b2bVerifyFormModalPartCompText}>
          Partner/Company
        </div>
        <Select
          menuPortalTarget={document.body}
          styles={customStyles}
          classNamePrefix="select"
          placeholder="Search by name"
          isDisabled={false}
          name="businessPartners"
          options={businessPartnerOptions}
          onChange={onBusinessPartnerChange}
          value={selectedBusinessPartner}
        />
        <div className={styles.b2bVerifyFormModalKeywordText}>Keyword</div>
        <Select
          menuPortalTarget={document.body}
          styles={customStyles}
          classNamePrefix="select"
          placeholder="Search by name"
          isDisabled={!selectedBusinessPartner}
          name="businessPartnerKeywords"
          options={selectedPartnerKeywordOptions}
          onChange={onKeywordChange}
          value={{
            label: selectedKeyword,
            value: selectedBusinessPartner?.value,
          }}
        />
      </Modal.Body>
      <Modal.Footer className={styles.b2bModalFooter}>
        <Button
          className={styles.b2bVerifyFormModalCancelBtn}
          onClick={() => handleChangeB2bFormModal(false)}
        >
          <div className={styles.b2bVerifyFormModalCancelBtnText}>Cancel</div>
        </Button>
        <Button
          className={styles.b2bVerifyFormModalVerifyBtn}
          onClick={handleSubmit}
          disabled={!selectedKeyword || !selectedBusinessPartnerName}
        >
          <div className={styles.b2bVerifyFormModalVerifyNowBtnText}>
            Verify now
          </div>
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default B2bVerifyFormModal
